import { Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import StarRating from '../../components/shared/StarRating';
import './RatePlayer.module.css';
import fetchAllTeams from '../../components/shared/fetchAllTeams';

interface Player {
  id: number;
  name: string;
  ratings?: number[];
  rating?: number;
}

interface Team {
  id: number;
  name: string;
  logo: string;
  players: Player[];
  // Add other properties if needed
}

interface RatePlayerProps {
  ratePlayer: (playerId: number, rating: number,) => void;
  fetchSquad: (teamId: number) => Promise<any>;
  updatePlayerRating: (playerId: number, rating: number, squadId: number) => Promise<any>;
}

export const RatePlayer: React.FC<RatePlayerProps> = ({ ratePlayer, fetchSquad, updatePlayerRating }) => {
  const [rating, setRating] = useState<number>(0);
  const [unratedPlayers, setUnratedPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const allTeams = await fetchAllTeams();
        setTeams(Array.isArray(allTeams) ? allTeams : []);
      } catch (err) {
        console.error('Failed to load teams', err);
        setTeams([]);
      }
    };

    fetchTeams();
  }, []);

  const handleRatingChange = (ratingValue: number) => {
    setRating(ratingValue);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const playerToRate = selectedPlayer;
    const team = selectedTeam;
    const ratingValue = rating;
    if (!playerToRate || !team || ratingValue === 0) return;
    try {
      await updatePlayerRating(playerToRate.id, ratingValue, team.id);
      await ratePlayer(playerToRate.id, ratingValue);
      const updatedUnrated = unratedPlayers.filter(p => p.id !== playerToRate.id);
      setUnratedPlayers(updatedUnrated);
      setSelectedPlayer(
        updatedUnrated.length > 0
          ? updatedUnrated[Math.floor(Math.random() * updatedUnrated.length)]
          : null
      );
      setRating(0);
    } catch (err) {
      console.error(err);
    }
  };

  // Load squad and derive unrated players (those with no ratings stored in DB)
  useEffect(() => {
    if (!selectedTeam) return;
    fetch(`/squads/${selectedTeam.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(squad => {
        const players = squad?.response?.[0]?.players;
        if (!Array.isArray(players) || players.length === 0) {
          setUnratedPlayers([]);
          setSelectedPlayer(null);
          return;
        }
        const unrated = players.filter((p: Player) => !p.ratings?.length);
        setUnratedPlayers(unrated);
        setSelectedPlayer(
          unrated.length > 0
            ? unrated[Math.floor(Math.random() * unrated.length)]
            : null
        );
      })
      .catch(() => {
        setUnratedPlayers([]);
        setSelectedPlayer(null);
      });
  }, [selectedTeam]);
  

  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
  };

  const teamLogoUrl = (team: Team) =>
    team.id != null ? `/api/team-logo/${team.id}` : '';

  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", marginTop: "80px" }}>
        {teams.length === 0 ? (
          <p style={{ color: '#666' }}>No teams loaded. Ensure the server is running and using the same database you populated (e.g. <code>MONGODB_URI</code> ending with <code>/test</code>).</p>
        ) : (
          teams.map((team) => (
            <Button key={team.id} onClick={() => handleTeamSelect(team)} sx={{fontSize: "22px", width: "100px", height: "50px" ,backgroundColor: "transparent", color: "blue"}}>
              <img src={teamLogoUrl(team)} alt={team.name} style={{ height: '50px', width: '50px', objectFit: 'contain' }} onError={(e) => { const t = e.target as HTMLImageElement; t.onerror = null; t.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><rect fill="#eee" width="50" height="50"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-size="14">?</text></svg>'); }} />
            </Button>
          ))
        )}
      </div>
      <h1>Rate Player</h1>
      {selectedTeam && (
        <p style={{ color: '#666', marginTop: 0 }}>
          {unratedPlayers.length === 0 && !selectedPlayer
            ? 'No players for this squad.'
            : `${unratedPlayers.length} unrated player${unratedPlayers.length !== 1 ? 's' : ''} in this squad`}
        </p>
      )}
      {selectedTeam && unratedPlayers.length === 0 && !selectedPlayer ? null : selectedPlayer ? (
        <form onSubmit={handleSubmit} style={{alignContent: "center", display: "grid"}}>
          <p style={{alignContent: "center"}}>{selectedPlayer.name}</p>
          <StarRating initialRating={rating} onRatingChange={handleRatingChange}/>
          <Button 
            type="submit"
            disabled={rating === 0}
            sx={{
              "&.Mui-selected": {},
              "&.Mui-focusVisible": {
                border: "3px solid #F2A42A"
              },
              ":focus": {
                border: "3px solid #F2A42A"
              },
              textTransform: "none",
              borderRadius: 3,
              fontSize: "22px"
            }}
          >Submit Rating</Button>
        </form>
      ) : (
        <p>All players have been rated!</p>
      )}
    </div>
  );
}

export default RatePlayer;
